var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { parseFamilyA, parseSubfieldA, parseSubfieldD, summarizeCorporateD, summarizeFamilyA, summarizeSubfieldA, summarizeSubfieldD, } from '../utils/summarization';
/** Encoder whitelist. Keys are the names a form config may put in `summarizeFn`. */
export var SUMMARIZE_CODECS = {
    summarizeSubfieldA: {
        args: ['FAMILY_NAME', 'GIVEN_NAME'],
        fn: function (family, given) { return summarizeSubfieldA(family, given); },
        defaultParseFn: 'parseSubfieldA',
    },
    summarizeSubfieldD: {
        args: ['BIRTH_DATE', 'DEATH_DATE'],
        fn: function (birth, death) { return summarizeSubfieldD(birth, death); },
        defaultParseFn: 'parseSubfieldD',
    },
    /**
     * CORPORATE `110`/`410` `$a` (CZNA-1412): the organization name and nothing
     * else. Every subordinate unit is a `$b` of its own, so there is nothing to
     * compose — the codec exists so `$a` keeps its component and, with it, the
     * CZNA-898 snapshot and affix (the ISBD period before `$b` is the suffix).
     */
    summarizeOrganizationName: {
        args: ['ORGANIZATION_NAME'],
        fn: function (org) { return org.trim(); },
        defaultParseFn: 'parseOrganizationName',
    },
    /**
     * LEGACY NAME, not a codec of its own (CZNA-1412). It used to compose
     * `ORGANIZATION_NAME. SUBORDINATE_UNIT` into `$a`, which put the first unit
     * into the heading twice. The name stays because customized templates still
     * reference it — test's `CORPORATE.default` (USER edit of 20. 10. 2026) and
     * named templates — and an unknown name empties `$a` (see `lookupSummarizeCodec`).
     * It now reads exactly like `summarizeOrganizationName`; the compiler strips
     * the `SUBORDINATE_UNIT` component those templates still declare on `$a`.
     */
    summarizeCorporateA: {
        args: ['ORGANIZATION_NAME'],
        fn: function (org) { return org.trim(); },
        defaultParseFn: 'parseCorporateA',
    },
    summarizeCorporateD: {
        args: ['FOUNDATION_DATE', 'DISSOLUTION_DATE'],
        fn: function (foundation, dissolution) { return summarizeCorporateD(foundation, dissolution); },
        defaultParseFn: 'parseSubfieldD',
    },
    /**
     * PERSONAL `100`/`400` family heading (`ind1='3'`). Component-driven since
     * CZNA-1007; it was `args: []` before, because `aQualifier` was a form-only
     * SIBLING SLOT rather than a component and the family branch of
     * `genericFormToProposalData` composed `$a` by hand. That is what made the
     * qualifier unrenderable: the renderer iterates a subfield's components, and
     * the qualifier was not one. It is now `FAMILY_QUALIFIER`, a `formOnly`
     * component of `$a` (see `ComponentDef.formOnly`) — form state only, never a
     * `*_data_component` row, so nothing about persistence changed.
     */
    summarizeFamilyA: {
        args: ['FAMILY_NAME', 'FAMILY_QUALIFIER'],
        fn: function (name, qualifier) { return summarizeFamilyA(name, qualifier); },
        defaultParseFn: 'parseFamilyA',
    },
};
/** Decoder whitelist. Keys are the names a form config may put in `parseFn`. */
export var PARSE_CODECS = {
    parseSubfieldA: function (raw, ind1) {
        var _a = parseSubfieldA(raw, ind1), FAMILY_NAME = _a.FAMILY_NAME, GIVEN_NAME = _a.GIVEN_NAME;
        return [FAMILY_NAME, GIVEN_NAME];
    },
    parseSubfieldD: function (raw) {
        var _a = parseSubfieldD(raw), first = _a.first, second = _a.second;
        return [first, second];
    },
    /** The whole `$a` is the name — never split on a period (CZNA-1412). */
    parseOrganizationName: function (raw) { return [raw]; },
    /** Legacy name of `parseOrganizationName`, kept for the same templates as `summarizeCorporateA`. */
    parseCorporateA: function (raw) { return [raw]; },
    parseFamilyA: function (raw) {
        var _a = parseFamilyA(raw), familyName = _a.familyName, qualifier = _a.qualifier;
        return [familyName, qualifier !== null && qualifier !== void 0 ? qualifier : ''];
    },
};
/**
 * One warning per distinct cause for the lifetime of the page. The lookups run
 * from the render path (`DynamicFieldPanel` re-renders on every keystroke), so
 * an unguarded `console.warn` would emit thousands of identical lines and bury
 * the one message that matters.
 */
var warnedNames = new Set();
var warnOnceRaw = function (key, message) {
    if (warnedNames.has(key))
        return;
    warnedNames.add(key);
    // eslint-disable-next-line no-console
    console.warn("[codecRegistry] ".concat(message));
};
var warnOnce = function (kind, name) { return warnOnceRaw("".concat(kind, ":").concat(name), "form config references unknown ".concat(kind, " \"").concat(name, "\" \u2014 subfield falls back to a plain value")); };
/**
 * The codec pair that actually applies to this subfield under `ind1`
 * (CZNA-1007). Every function below resolves through this rather than reading
 * `sf.summarizeFn` directly, which is what lets `100$a` decompose into
 * `{FAMILY_NAME, GIVEN_NAME}` for `ind1='1'` and `{FAMILY_NAME,
 * FAMILY_QUALIFIER}` for `ind1='3'` without a `tag === '100' && ind1 === '3'`
 * test anywhere outside the template.
 *
 * <p>An override replaces the PAIR. Inheriting the base `parseFn` under an
 * overridden `summarizeFn` would decode with one codec and encode with
 * another; the entry may still omit its own `parseFn`, and the new encoder's
 * `defaultParseFn` is then used exactly as on the base path.
 *
 * <p>The omission is one-way: an entry may skip `parseFn`, never `summarizeFn`.
 * The encoder entry is what carries the component names and the default
 * decoder, so a decoder-only override configures nothing at all — see the
 * fallback below.
 */
export var resolveCodecNames = function (sf, ind1) {
    var byInd1 = sf.codecByInd1;
    if (!byInd1)
        return { summarizeFn: sf.summarizeFn, parseFn: sf.parseFn };
    if (ind1 === undefined) {
        // Tolerant, not silent (ADR-0005 D7): falling back to the base pair is the
        // safe reading, but a caller that forgot to thread `ind1` would otherwise
        // encode a family heading with the surname codec and leave no trace.
        warnOnceRaw('codecByInd1:no-ind1', 'a subfield declares codecByInd1 but the caller supplied no ind1 — using the base codec pair');
        return { summarizeFn: sf.summarizeFn, parseFn: sf.parseFn };
    }
    var override = byInd1[ind1];
    if (!override)
        return { summarizeFn: sf.summarizeFn, parseFn: sf.parseFn };
    if (!override.summarizeFn) {
        // A decoder-only override is not a half-configured pair, it is NOTHING:
        // the encoder entry is what carries the component names and the default
        // decoder, so without it `computeSummarizedValue` composes '' and
        // `parseSummarizedValue` has no slots to map positions onto — the subfield
        // empties on load AND on save, silently. `form-config.schema.json` now
        // requires `summarizeFn`, which closes the write path; this closes the read
        // path for anything already stored, and warns rather than repairing
        // quietly (ADR-0005 D7).
        warnOnceRaw("codecByInd1:no-summarizeFn:".concat(ind1), "a codecByInd1 override for ind1='".concat(ind1, "' names no summarizeFn \u2014 using the base codec pair"));
        return { summarizeFn: sf.summarizeFn, parseFn: sf.parseFn };
    }
    return { summarizeFn: override.summarizeFn, parseFn: override.parseFn };
};
/**
 * The renderer's visibility predicate, shared (CZNA-1007).
 *
 * <p>It has to be one function: the save path decides whether a subfield's
 * visible component set contains a `formOnly` slot, the gate test counts the
 * components visible under an indicator, and the renderer draws them. Three
 * readings of one rule is how `100$a` ended up with a component that was
 * parsed, composed and never drawn.
 */
export var matchesVisibleWhen = function (predicate, currentInd1) {
    if (!predicate || !predicate.ind1)
        return true;
    return predicate.ind1.includes(currentInd1);
};
/** The components of `sf` that render under `ind1`, in declaration order. */
export var visibleComponents = function (sf, ind1) { return (sf.components || []).filter(function (c) { return matchesVisibleWhen(c.visibleWhen, ind1 || ''); }); };
/**
 * The `ind1` a field-instance's `visibleWhen` predicates are judged against:
 * the live value, else the field's default. `DynamicFieldPanel` draws with this
 * and `NameTypeIndicatorControl` clears with it, so what the cataloger sees is
 * what a heading-type switch treats as "was there".
 */
export var visibilityInd1 = function (fieldDef, liveInd1) { var _a; return liveInd1 || ((_a = fieldDef.indicator1Def) === null || _a === void 0 ? void 0 : _a.defaultValue) || fieldDef.indicator1 || ''; };
/**
 * The slots of `fieldDef` that do NOT render under `ind1` (CZNA-485) — the
 * heading-type switch's clear-map, read off the config's `visibleWhen`.
 *
 * <p>It used to be a hand-written `switch` in `NameTypeIndicatorControl` naming
 * PERSONAL's `100`/`400` slots per indicator, i.e. a second copy of the
 * predicates the renderer already reads from the config. The two agreed only
 * because nobody had edited one without the other yet; CZNA-1007 had to edit
 * both when the family qualifier moved into `$a`.
 *
 * <p>A subfield whose own `visibleWhen` fails is one slot (its components go
 * with it). Inside a visible subfield, a component TYPE is hidden only when no
 * declaration of it matches — `100$a` declares `FAMILY_NAME` twice, once per
 * heading type, and it is the same slot in form state both times.
 */
export var hiddenHeadingTypeSlots = function (fieldDef, ind1) {
    var hidden = [];
    for (var _i = 0, _a = fieldDef.subfields; _i < _a.length; _i++) {
        var sf = _a[_i];
        if (!matchesVisibleWhen(sf.visibleWhen, ind1)) {
            hidden.push({ code: sf.code });
        }
        else {
            var shown = new Set(visibleComponents(sf, ind1).map(function (c) { return c.componentType; }));
            var declared = Array.from(new Set((sf.components || []).map(function (c) { return c.componentType; })));
            for (var _b = 0, declared_1 = declared; _b < declared_1.length; _b++) {
                var componentType = declared_1[_b];
                if (!shown.has(componentType))
                    hidden.push({ code: sf.code, componentType: componentType });
            }
        }
    }
    return hidden;
};
/**
 * Whether this subfield's visible component set under `ind1` includes a slot
 * that never reaches the API (CZNA-1007). Two consequences, both of which used
 * to be spelled `tag in {100,400} && ind1 === '3'`:
 *
 * <ul>
 * <li><b>Load.</b> The persisted component rows cannot represent the split, so
 *     the flat value is the authority and has to be decoded. On a family
 *     heading the stored `FAMILY_NAME` is the WHOLE `$a` — the ontology binds
 *     it `part: wholeValue` — so seeding from it would put `Rottovi (rodina)`
 *     in the name box and leave the qualifier box empty.</li>
 * <li><b>Save.</b> An edited row emits no `components[]` at all. Writing our
 *     split (`FAMILY_NAME = Rottovi`) would contradict that same bind, which is
 *     why the family branch never wrote one either.</li>
 * </ul>
 */
export var hasFormOnlyComponent = function (sf, ind1) { return visibleComponents(sf, ind1).some(function (c) { return c.formOnly; }); };
/** Test seam: the warn-once set is module state and would leak across cases. */
export var __resetCodecWarnings = function () { return warnedNames.clear(); };
/** Resolves an encoder name against the whitelist; `null` (with one warning) when unknown. */
export var lookupSummarizeCodec = function (name) {
    if (!name)
        return null;
    var codec = SUMMARIZE_CODECS[name];
    if (!codec) {
        warnOnce('summarizeFn', name);
        return null;
    }
    return codec;
};
/** Resolves a decoder name against the whitelist; `null` (with one warning) when unknown. */
export var lookupParseCodec = function (name) {
    if (!name)
        return null;
    var codec = PARSE_CODECS[name];
    if (!codec) {
        warnOnce('parseFn', name);
        return null;
    }
    return codec;
};
/** The codec's own output, with no affix reattached. */
var composeCore = function (sf, componentValues, ind1) {
    var codec = lookupSummarizeCodec(resolveCodecNames(sf, ind1).summarizeFn);
    if (!codec || codec.args.length === 0)
        return '';
    return codec.fn.apply(codec, codec.args.map(function (arg) { return componentValues[arg] || ''; }));
};
/**
 * Computes a subfield's flat value from its component map, using the codec the
 * config names. Returns `''` when there is no codec, the name is unknown, or
 * the codec is not component-driven (`args: []`) — the historical behaviour of
 * `computeSummarizedValue`, kept because callers treat `''` as "nothing to
 * emit" and a component subfield with no components must not persist a value.
 *
 * Shared by the renderer (`DynamicFieldPanel`) and the save mapper
 * (`genericFormToProposalData`) so the preview and the persisted `$a` cannot
 * drift apart.
 */
export var computeSummarizedValue = function (sf, componentValues, affix, ind1) {
    var _a, _b;
    var core = composeCore(sf, componentValues, ind1);
    if (!core)
        return '';
    // CZNA-1412 — the stored `$a Univerzita Karlova.` keeps its ISBD period as
    // suffix; a name typed as `Acme Inc.` already ends a sentence and takes no
    // second one (`Acme Inc..`). Only an edited name gets here with an affix.
    var suffix = (_a = affix === null || affix === void 0 ? void 0 : affix.suffix) !== null && _a !== void 0 ? _a : '';
    var joined = suffix.startsWith('.') && /[.?!]$/.test(core) ? suffix.slice(1) : suffix;
    return "".concat((_b = affix === null || affix === void 0 ? void 0 : affix.prefix) !== null && _b !== void 0 ? _b : '').concat(core).concat(joined);
};
var NO_AFFIX = { prefix: '', suffix: '' };
/**
 * Peels the affix off a stored value by comparing it with what the components
 * compose to (CZNA-898). The inverse of the `affix` argument to
 * {@link computeSummarizedValue}, so `compute(sf, comps, derive(sf, comps, v))`
 * is `v` for every value this can decide.
 *
 * <p><b>It reports only what it can prove, and that is the whole design.</b> The
 * core must occur in the stored value EXACTLY ONCE — otherwise which occurrence
 * is the core is a guess, and a wrong split reattaches the wrong text to an
 * edited name forever. `null` for anything undecidable (no core, core absent,
 * core ambiguous), and then the subfield is protected only by the unedited
 * guard, which is exact. Deliberately NOT a rule about commas or parentheses:
 * a rule invented from the two shapes above would rewrite the 17 294 `100$a`
 * values that have a `$d` and legitimately carry no comma.
 */
export var deriveComponentAffix = function (sf, componentValues, storedValue, ind1) {
    var core = composeCore(sf, componentValues, ind1);
    if (!core)
        return null;
    if (storedValue === core)
        return NO_AFFIX;
    var first = storedValue.indexOf(core);
    if (first === -1)
        return null;
    if (storedValue.indexOf(core, first + 1) !== -1)
        return null;
    return { prefix: storedValue.slice(0, first), suffix: storedValue.slice(first + core.length) };
};
/**
 * Compared verbatim, without trimming: a component whose stored value has
 * surrounding whitespace is still unedited while that whitespace is on screen,
 * and trimming here would report it as an edit and hand the row to the codec —
 * which is the path the guard exists to keep it off.
 */
export var snapshotUnedited = function (snapshot, current, onlyTypes) {
    var _a, _b;
    var types = new Set(onlyTypes
        ? onlyTypes.concat(Object.keys(current))
        : __spreadArray(__spreadArray([], Object.keys(snapshot), true), Object.keys(current), true));
    // Array.from: the production stripes build target lacks downlevelIteration,
    // so a Set cannot be iterated directly (TS2802).
    for (var _i = 0, _c = Array.from(types); _i < _c.length; _i++) {
        var type = _c[_i];
        if (((_a = snapshot[type]) !== null && _a !== void 0 ? _a : '') !== ((_b = current[type]) !== null && _b !== void 0 ? _b : ''))
            return false;
    }
    return true;
};
/**
 * Inverse of {@link computeSummarizedValue} (CZNA-310): decomposes a flat
 * subfield value into its component map so imported (Aleph) records — stored
 * as flat strings with no `components[]` metadata — populate the per-component
 * inputs on edit. Without it a no-op save drops the heading, because the
 * encoder skips component subfields whose component map is entirely empty.
 *
 * The decoder is `sf.parseFn` when the config names one, otherwise the
 * encoder's `defaultParseFn`. Component names always come from the ENCODER's
 * `args`, which is what lets one splitter serve two different component pairs.
 *
 * Returns `{}` when there is no codec, no decoder, the codec is not
 * component-driven, or the value is blank — callers then leave the slots empty.
 */
export var parseSummarizedValue = function (sf, rawValue, ind1) {
    var _a;
    var names = resolveCodecNames(sf, ind1);
    var codec = lookupSummarizeCodec(names.summarizeFn);
    if (!codec || codec.args.length === 0)
        return {};
    var parseName = (_a = names.parseFn) !== null && _a !== void 0 ? _a : codec.defaultParseFn;
    var parse = lookupParseCodec(parseName);
    if (!parse)
        return {};
    var raw = (rawValue || '').trim();
    if (!raw)
        return {};
    var values = parse(raw, ind1);
    var decoded = {};
    codec.args.forEach(function (arg, i) {
        var _a;
        decoded[arg] = (_a = values[i]) !== null && _a !== void 0 ? _a : '';
    });
    return decoded;
};
/**
 * What a component subfield READS AS — the one rule every caller must use
 * (CZNA-898).
 *
 * <p>Three answers, in the order `genericFormToProposalData` decides what to
 * PERSIST, because anything that DISPLAYS the value has to ask the same
 * question in the same order or it will show something the save does not write:
 *
 * <ol>
 * <li>the row arrived FLAT and unedited → the imported original (CZNA-310);</li>
 * <li>the row arrived WITH components and is unedited → the stored row
 *     (CZNA-898);</li>
 * <li>edited → what the codec composes, with the stored framing reattached.</li>
 * </ol>
 *
 * <p><b>The order is not cosmetic and neither is having one function.</b> This
 * ticket's first version gave the renderer the affix but not the snapshot, so
 * wherever the affix was underivable the panel composed a bare core while an
 * unedited save emitted the stored row. Its second version added the snapshot
 * but left CZNA-310 out, so on an imported `Novák,` the panel still showed
 * `Novák` while the save wrote `Novák,` — the same defect, on the other half of
 * a mutually exclusive pair. Both were found by review, in previews that each
 * re-derived the rule for themselves; that is the argument for this function.
 *
 * <p>Returns `''` for a subfield with no codec or no components, unchanged from
 * {@link computeSummarizedValue} — callers treat that as "nothing to emit".
 */
export var readComponentSubfield = function (sf, componentValues, originals, ind1) {
    var imported = originals === null || originals === void 0 ? void 0 : originals.importedValue;
    if (imported !== undefined) {
        var decomposed = parseSummarizedValue(sf, imported, ind1);
        if (snapshotUnedited(decomposed, componentValues, Object.keys(decomposed)))
            return imported;
    }
    var persisted = originals === null || originals === void 0 ? void 0 : originals.persisted;
    if (persisted && snapshotUnedited(persisted.declared, componentValues))
        return persisted.value;
    return computeSummarizedValue(sf, componentValues, persisted, ind1);
};
